import { Navigate, useOutlet, useLocation } from 'react-router-dom'
import { useIdleTimer } from 'react-idle-timer'
import { useAuthContext } from '../../contexts/AuthContext'
import { useAuth } from '../../hooks/useAuth'
import { useSurveyGuard } from '../../hooks/useSurveyGuard'
import { toast } from 'react-toastify'
import { ReactElement } from 'react'
import { logger } from '../../utils'

export const ProtectedRoute = ({ children }: any): ReactElement | null => {
  const outlet = useOutlet()
  const location = useLocation()
  const { userProfile, setUserProfile } = useAuth()
  const { signout } = useAuthContext()

  // Pages that handle their own survey redirect logic
  const surveyGuardExcludedPaths = ['/presurvey', '/postsurvey', '/user-dashboard/completed', '/user-dashboard/congrats']
  const skipSurveyGuard = surveyGuardExcludedPaths.includes(location.pathname)
  const { loading: surveyLoading, pendingSurvey } = useSurveyGuard({ skip: skipSurveyGuard })
  const timeout: number = 1000 * 60 * 20 // 20 min.  Smaller values increase risk of timeout during video / unity play.
  const onPrompt = (): void => {
    // Fire a Modal Prompt
  }
  const onIdle = (): void => {
    signout()
      .then(() => {
        setUserProfile(null)
        window.location.href = '/'
      })
      .catch(error => toast.error('Error signing user out', { position: toast.POSITION.TOP_CENTER }))
  }
  const onActive = (): void => {
    // Close Modal Prompt
    // Do some active action
  }
  const onAction = (): void => {
    // Do something when a user triggers a watched event
  }

  // https://idletimer.dev/docs/api/props for list of props, etc.
  useIdleTimer({
    onPrompt,
    onIdle,
    onActive,
    onAction,
    timeout,
    promptTimeout: 0,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
      'visibilitychange'
    ],
    immediateEvents: [],
    debounce: 0,
    throttle: 0,
    eventsThrottle: 200,
    element: document,
    startOnMount: true,
    startManually: false,
    stopOnIdle: false,
    crossTab: false,
    name: 'idle-timer',
    syncTimers: 0,
    leaderElection: false
  })

  if (userProfile === null) {
    return <Navigate to='/' />
  }
  if (!userProfile.user_profile.accepted_terms) {
    return <Navigate to='/terms-of-use' />
  }
  // Check for pending surveys (skip if already on survey pages)
  if (!skipSurveyGuard) {
    if (surveyLoading) {
      logger.log('ProtectedRoute: survey guard loading', { pathname: location.pathname })
      return null
    }
    if (pendingSurvey === 'presurvey') {
      logger.log('ProtectedRoute: redirecting to survey', { pendingSurvey, pathname: location.pathname })
      return <Navigate to='/presurvey' replace />
    }
    if (pendingSurvey === 'postsurvey') {
      logger.log('ProtectedRoute: redirecting to survey', { pendingSurvey, pathname: location.pathname })
      return <Navigate to='/postsurvey' replace />
    }
  }
  return outlet
}
